import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { isPreOrderModifiable } from "../../services/recapService";
import type { FlowCellProps } from "./types";
import { CELL_INPUT } from "./styles";
import { cn } from "@/lib/utils";
import { AI_PENDING_CELL_CLASS } from "./AiChangeBadge";
import { InlineCustomFieldSelect } from "./InlineCustomFieldSelect";
import { ExpandableTextCell } from "../ui/ExpandableTextCell";
import {
  AiCustomFieldBadge,
  formatCustomFieldValue,
  isAiPendingCustomField,
} from "./AiCustomFieldBadge";

interface InlineCustomFieldCellProps extends FlowCellProps {
  disabled?: boolean;
}

type CustomFieldValues = Record<string, string | number | boolean>;

/**
 * Inline editor for a single custom field, rendered as its own table column.
 * Supports text, number, select, and checkbox field types inline. The field
 * definition comes from `column.customFieldDef` (set by
 * `buildInlineCustomFieldColumns`). Reads/writes `flow.flow_metadata[key]`.
 *
 * Replaces the popover-based `CustomFieldsCell` when inline columns are
 * generated — each field gets its own column instead of collapsing into one
 * popover. `FlowRow` routes any column with `customFieldDef` here.
 */
export function InlineCustomFieldCell({
  flow,
  preOrder,
  column,
  disabled,
  onFlowUpdate,
}: Readonly<InlineCustomFieldCellProps>) {
  const { t } = useTranslation();
  const fieldDef = column?.customFieldDef;
  const key = fieldDef?.key;
  const isLocked = disabled || !isPreOrderModifiable(preOrder);

  const metadata = (flow.flow_metadata ?? {}) as CustomFieldValues;
  const value = key ? metadata[key] : undefined;

  const handleChange = useCallback(
    (newValue: string | number | boolean | null) => {
      if (!key) return;
      onFlowUpdate(flow.id, {
        flow_metadata: {
          ...flow.flow_metadata,
          [key]: newValue,
        },
      });
    },
    [onFlowUpdate, flow.id, flow.flow_metadata, key],
  );

  if (!fieldDef || !key) return null;

  const def = fieldDef;
  const fieldKey = key;

  const control = (() => {
    switch (def.type) {
      case "select":
        return (
          <InlineCustomFieldSelect
            fieldKey={fieldKey}
            value={(value as string) ?? ""}
            options={def.options ?? []}
            onChange={handleChange}
            disabled={isLocked}
            placeholder={def.label}
            clearLabel={t("common.none", "None")}
          />
        );

      case "number":
        return (
          <input
            type="text"
            inputMode="decimal"
            data-testid={`flow-custom-field-${fieldKey}-input`}
            data-tab-cell
            value={value != null && value !== "" ? String(value) : ""}
            onChange={(e) => {
              const raw = e.target.value;
              if (raw === "") {
                handleChange(null);
              } else {
                const parsed = parseFloat(raw);
                handleChange(Number.isNaN(parsed) ? null : parsed);
              }
            }}
            onFocus={(e) => e.target.select()}
            placeholder="0"
            disabled={isLocked}
            className={cn(CELL_INPUT)}
          />
        );

      case "text":
        // Free text is the one field type whose value has no natural width, so
        // it gets the cell that grows: `…` marker when clipped, hover peek,
        // and a full-size editor over the cell on focus. See
        // `ExpandableTextCell` — the inline input, its testid and the grid
        // keyboard contract are unchanged.
        return (
          <ExpandableTextCell
            testId={`flow-custom-field-${fieldKey}-input`}
            value={(value as string) ?? ""}
            onChange={handleChange}
            placeholder={def.label}
            label={def.label}
            disabled={isLocked}
          />
        );

      case "date":
        // Values are stored as ISO `YYYY-MM-DD` strings — exactly what a
        // native date input reads and emits, so no formatting round-trip.
        // Clearing the input writes null rather than "" so the key reads as
        // absent (matches the number branch).
        return (
          <input
            type="date"
            data-testid={`flow-custom-field-${fieldKey}-input`}
            data-tab-cell
            value={(value as string) ?? ""}
            onChange={(e) => handleChange(e.target.value || null)}
            disabled={isLocked}
            className={cn(CELL_INPUT)}
          />
        );

      case "checkbox":
        return (
          <div className="flex items-center justify-center h-7">
            <input
              type="checkbox"
              data-testid={`flow-custom-field-${fieldKey}-checkbox`}
              checked={!!value}
              onChange={(e) => handleChange(e.target.checked)}
              disabled={isLocked}
              className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:cursor-not-allowed"
            />
          </div>
        );

      default:
        return null;
    }
  })();

  // Mode-independent guide handles (mirrors CustomFieldsCell): a generic
  // `flow-custom-fields` (any custom field) plus a PER-FIELD
  // `flow-custom-field-<key>` so the guidance overlay can spotlight one
  // specific field. Both resolve whether the fields render inline (≥1280px) or
  // collapse into the popover (<1280px, where CustomFieldsCell mirrors these).
  const guideHandle = {
    "data-attr": "flow-custom-fields",
    "data-testid": `flow-custom-field-${fieldKey}`,
  } as const;

  if (!isAiPendingCustomField(flow, fieldKey))
    return (
      <div {...guideHandle} className="flex min-w-0 items-center w-full">
        {control}
      </div>
    );

  // Surface the AI's proposed value as a "from → new" badge below the field,
  // matching the numeric/unit cells. Values persist to the flat
  // `flow_metadata[key]` lane, so `AiCustomFieldBadge` resolves the diff from
  // the staged `flowMetadata` blob. `format` maps a select value to its label.
  const format = (v: unknown): string => formatCustomFieldValue(def, v, t);

  // Block wrapper (not `span`) — the checkbox control renders a `div`, so a
  // `span` here would be invalid DOM nesting.
  return (
    <div className="flex min-w-0 flex-col">
      <div
        {...guideHandle}
        className={cn("flex min-w-0 items-center", AI_PENDING_CELL_CLASS)}
      >
        {control}
      </div>
      <AiCustomFieldBadge flow={flow} fieldKey={fieldKey} format={format} />
    </div>
  );
}
